import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import MaterialTypeDropdown from "./MaterialTypeDropdown";
import ColorDropdown from "./ColorDropdown";
import SizeDropdown from "./SizeDropdown";
import { fetchInventoryRecords } from "../../services/MaterialAPI";
import { UserContext } from "../../context/UserContext";

// Define a generic interface for the props
interface MaterialTypeSelectorProps<T> {
    onSelect: (filters: {
        material_type_id: number | null;
        color_code: string | null;
        material_size_id: number | null;
        records: T[]; // Generic records type
    }) => void;
    selectedValue?: {
        material_type_id: number | null;
        color_code: string | null;
        material_size_id: number | null;
    }; // New prop for pre-selecting values
    required?: boolean;
}

const MaterialTypeSelector = <T, >({
                                       onSelect,
                                       selectedValue,
                                       required,
                                   }: MaterialTypeSelectorProps<T>) => {
    const [ materialTypeId, setMaterialTypeId ] = useState<number | null>(
        selectedValue?.material_type_id || null,
    );
    const [ colorCode, setColorCode ] = useState<string | null>(
        selectedValue?.color_code || null,
    );
    const [ materialSizeId, setMaterialSizeId ] = useState<number | null>(
        selectedValue?.material_size_id || null,
    );
    const [ inventoryRecords, setInventoryRecords ] = useState<T[]>([]);
    const [ colorOptions, setColorOptions ] = useState<{ label: string; value: string }[]>([]);
    const prevMaterialTypeId = useRef(materialTypeId);
    const prevMaterialSizeId = useRef(materialSizeId);
    const prevColorCode = useRef(colorCode);
    const { isLoggedIn } = useContext(UserContext);

    // Fetch inventory records on initial load
    useEffect(() => {
        const fetchRecords = async () => {
            try {
                const response = await fetchInventoryRecords(); // Assume this function does not need generics
                setInventoryRecords(response || []);
            } catch (error) {
                console.error("Failed to fetch inventory records:", error);
            }
        };

        fetchRecords();
    }, [ isLoggedIn ]);

    // Memoized filtering logic to optimize performance
    const filterRecords = useCallback(() => {
        let filteredRecords = inventoryRecords;

        if (materialTypeId !== null) {
            filteredRecords = filteredRecords.filter((record: any) => record.material_type_id === materialTypeId);
        }

        if (materialSizeId !== null) {
            filteredRecords = filteredRecords.filter((record: any) => record.material_size_id === materialSizeId);
        }

        if (colorCode !== null) {
            filteredRecords = filteredRecords.filter((record: any) => record.color_code === colorCode);
        }

        return filteredRecords;
    }, [ materialTypeId, materialSizeId, colorCode, inventoryRecords ]);

    // Update color options and notify parent component on changes
    useEffect(() => {
        const filteredRecords = filterRecords();

        // Extract unique color options
        const colors = Array.from(
            new Map(
                filteredRecords.map((record: any) => [
                    record.color_code,
                    { label: `${record.color_name} (${record.color_code})`, value: record.color_code },
                ]),
            ).values(),
        );

        setColorOptions(colors);

        // Notify parent component if any state changes
        if (
            materialTypeId !== prevMaterialTypeId.current ||
            colorCode !== prevColorCode.current ||
            materialSizeId !== prevMaterialSizeId.current
        ) {
            onSelect({
                material_type_id: materialTypeId,
                color_code: colorCode,
                material_size_id: materialSizeId,
                records: filteredRecords,
            });
        }

        // Update references
        prevMaterialTypeId.current = materialTypeId;
        prevMaterialSizeId.current = materialSizeId;
        prevColorCode.current = colorCode;
    }, [ materialTypeId, materialSizeId, colorCode, filterRecords, onSelect ]);

    // Handlers for dropdown changes
    const handleMaterialTypeChange = (typeId: number | null) => {
        setMaterialTypeId(typeId);
        setColorCode(null); // Reset color when material type changes
    };

    const handleColorChange = (color: string | null) => {
        setColorCode(color);
    };

    const handleSizeChange = (sizeId: number | null) => {
        setMaterialSizeId(sizeId);
        setColorCode(null); // Reset color when size changes
    };

    return (
        <div className="flex flex-col">
            <MaterialTypeDropdown required={required} value={materialTypeId} onChange={handleMaterialTypeChange}/>
            <SizeDropdown required={required} selectedSize={materialSizeId} onSelectSize={handleSizeChange}/>
            <ColorDropdown required={required} colorOptions={colorOptions} selectedColor={colorCode}
                           onSelectColor={handleColorChange}/>
        </div>
    );
};

export default MaterialTypeSelector;